<template>
  <b-modal
    lazy
    centered
    :visible="show"
    size="lg"
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-row>
      <b-col>
        <b-btn v-b-modal.add-item-modal size="sm" class="float-right">
          {{ $t("globals.add_item") }}
        </b-btn>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          caption-top small
          :items="selectedItemsTable.items"
          :fields="selectedItemsTable.fields"
        >
          <template #table-caption>{{ $t("globals.items") }}</template>
          <template v-slot:row-details="row">
            <ul style="margin-top: -10px;">
              <li
                v-for="synatecProduct in row.item.synatec_products"
                :key="synatecProduct.id"
              >
                <span class="text-secondary small">
                  {{ `${synatecProduct.name} - ${synatecProduct.synatec_code}` }}
                </span>
              </li>
            </ul>
          </template>
          <template v-slot:cell(quantity)="row">
            <b-form-input
              style="width: 80px"
              size="sm" type="number"
              v-model.number="row.item.quantity"
            ></b-form-input>
          </template>
          <template v-slot:cell(actions)="row">
            <b-btn
              v-if="row.item.synatec_products.length > 0"
              size="sm"
              @click="removeItem(row.item)"
              pill
            >
              <v-icon name="minus"></v-icon>
            </b-btn>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <hr />
    <!-- Address / Notes -->
    <b-row>
      <b-col>
        <h6 class="text-info">{{ $t("globals.send_to") }}:</h6>
      </b-col>
    </b-row>
    <b-row class="bg-light p-2 rounded">
      <b-col>
        <span class="text-secondary small">{{ $t("globals.prefill_data") }}:</span>
      </b-col>
      <b-col v-for="(dataSet, key) in prefillData" :key="key">
        <b-btn class="float-right" size="sm" variant="info" @click="prefillDataSet(dataSet)" >
          {{ key.replace('_', ' ').toUpperCase() }}
        </b-btn>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <span>{{ $t("globals.first_name") }}:</span>
        <span class="float-right">
          {{ $t("globals.last_name") }}:
        </span>
        <b-input-group>
          <b-form-input v-model="order.customer.shipping_first_name"/>
          <b-form-input v-model="order.customer.shipping_last_name"/>
        </b-input-group>
        <span>{{ $t("globals.address") }}:</span>
        <b-form-input v-model="order.customer.shipping_address"/>
        <span>{{ $t("globals.city") }}:</span>
        <b-form-input v-model="order.customer.shipping_city"/>
        <span>{{ $t("globals.postal_code") }}:</span>
        <span class="float-right"
          >{{ $t("globals.country") }}:</span
        >
        <b-input-group>
          <b-form-input v-model="order.customer.shipping_postal_code"/>
          <b-form-input
            v-model="order.customer.shipping_country"
            disabled
          />
        </b-input-group>
      </b-col>
      <b-col>
        <span>{{ $t("globals.email") }}:</span>
        <b-form-input v-model="order.customer.shipping_email"/>
        <label> {{ $t("globals.notes") }}: </label>
        <b-form-textarea v-model="notes" rows="6">
        </b-form-textarea>
      </b-col>
    </b-row>
    <hr />
    <b-row>
      <b-col>
        <b-btn size="sm" @click="close" variant="warning">
          {{ $t("globals.cancel") }}
        </b-btn>
      </b-col>
      <b-col>
        <b-btn
          size="sm"
          class="float-right"
          variant="success"
          @click="save"
          :disabled="saveDisabled"
        >
          {{ $t("globals.save") }}
        </b-btn>
      </b-col>
    </b-row>
    <!-- Add item modal -->
    <b-modal
      id="add-item-modal"
      title="Add Billing Item"
      centered
      size="md"
      hide-footer
    >
      <b-row>
        <b-col>
          <!-- Filter Row -->
          <b-row>
            <b-col class="my-3">
              <b-input-group>
                <b-form-input
                  v-model="billingItemsTable.filter"
                  :placeholder="$t('globals.type_to_search')"
                />
                <b-input-group-append>
                  <b-btn
                    variant="warning"
                    :disabled="!billingItemsTable.filter"
                    @click="billingItemsTable.filter = ''"
                  >
                    {{ $t("globals.clear") }}
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
            </b-col>
          </b-row>
          <!-- Billing Items Table -->
          <b-row>
            <b-col>
              <b-table
                small
                :items="billingItemsTable.items"
                :fields="billingItemsTable.fields"
                :sort-by.sync="billingItemsTable.sortBy"
                :sort-desc.sync="billingItemsTable.sortDesc"
                :filter="billingItemsTable.filter"
                :current-page="billingItemsTable.currentPage"
                :per-page="billingItemsTable.perPage"
                @filtered="billingItemsTable.currentPage = 1"
              >
                <template v-slot:cell(actions)="row">
                  <b-btn
                    v-if="row.item.synatec_products.length > 0"
                    size="sm"
                    @click="addBillingItem(row.item)"
                    pill
                  >
                    <v-icon name="plus"></v-icon>
                  </b-btn>
                </template>
                <template v-slot:row-details="row">
                  <ul style="margin-top: -10px;">
                    <li
                      v-for="synatecProduct in row.item.synatec_products"
                      :key="synatecProduct.id"
                    >
                      <span class="text-secondary small">
                        {{ `${synatecProduct.name} - ${synatecProduct.synatec_code}` }}
                      </span>
                    </li>
                  </ul>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <!-- Pagination -->
          <b-row>
            <b-col md="6" class="my-1">
              <b-pagination
                :total-rows="billingItemsTable.items.length"
                :per-page="billingItemsTable.perPage"
                v-model="billingItemsTable.currentPage"
                class="my-0"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-modal>
  </b-modal>
</template>

<script>
import { mapActions } from "vuex";

export default {
  props: {
    show: Boolean,
    close: Function,
    order: Object,
    prefillData: Object
  },
  data() {
    return {
      notes: "",
      billingItemsTable: {
        items: [],
        sortBy: "id",
        sortDesc: true,
        perPage: 4,
        currentPage: 1,
        fields: [
          {
            key: "item_name",
            label: this.$t("globals.item_name")
          },
          {
            key: "item_type",
            label: this.$t("globals.type")
          },
          "actions"
        ]
      },
      selectedItemsTable: {
        items: [],
        fields: [
          {
            key: "item_name",
            label: this.$t("globals.item_name")
          },
          {
            key: "quantity",
            label: this.$t("globals.quantity")
          },
          "actions"
        ]
      },

    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch"]),
    save() {
      let variables = {
        synatec_order: {
          order_id: parseInt(this.$route.params.id),
          customer_id: this.order.customer.id,
          notes: this.notes,
          shipping_first_name: this.order.customer.shipping_first_name,
          shipping_last_name: this.order.customer.shipping_last_name,
          shipping_email: this.order.customer.shipping_email,
          shipping_address: this.order.customer.shipping_address,
          shipping_city: this.order.customer.shipping_city,
          shipping_postal_code: this.order.customer.shipping_postal_code,
          shipping_country: this.order.customer.shipping_country,
          items: []
        }
      };

      this.selectedItemsTable.items.forEach(bi => {
        bi.synatec_products.forEach(sp => {
          variables.synatec_order.items.push({
            name: sp.name,
            synatec_code: sp.synatec_code,
            quantity: bi.quantity
          });
        });
      });

      let data = {
        query: `
          mutation SynatecOrderCreate ($synatec_order: SynatecOrderInput) {
            res: SynatecOrderCreate (synatec_order: $synatec_order) {
              id
            }
          }
        `,
        variables
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.close();
      });
    },
    prefillDataSet(dataSet) {
      this.order.customer.shipping_first_name = dataSet.firstName;
      this.order.customer.shipping_last_name = dataSet.lastName;
      this.order.customer.shipping_email = dataSet.email;
      this.order.customer.shipping_address = dataSet.address;
      this.order.customer.shipping_city = dataSet.city;
      this.order.customer.shipping_postal_code = dataSet.postalCode;
      this.order.customer.shipping_country = dataSet.country;
    },
    addBillingItem(item) {
      item.quantity = 1;
      this.selectedItemsTable.items.push(item);
      this.$bvModal.hide("add-item-modal");
    },
    removeItem(item) {
      this.selectedItemsTable.items = this.selectedItemsTable.items.filter(
        el => el.id !== item.id
      );
    },
    fetchBillingItems() {
      const data = {
        query: `
          query {
            billingItems {
              id
              item_name
              item_type
              _showDetails: default_true
              synatec_products {
                id
                name
                synatec_code
              }
            }
          }
        `
      };

      this.simpleFetch({ data, this_ref: this }).then(res => {
        if (res) {
          this.billingItemsTable.items = res.billingItems.filter(
            bi => bi.synatec_products.length > 0
          );
        }
      });
    }
  },
  computed: {
    saveDisabled() {
      return (
        this.selectedItemsTable.items.length === 0 ||
        this.order.customer.shipping_first_name.length == 0 ||
        this.order.customer.shipping_last_name.length == 0 ||
        this.order.customer.shipping_email.length == 0 ||
        this.order.customer.shipping_address.length == 0 ||
        this.order.customer.shipping_city.length == 0 ||
        this.order.customer.shipping_postal_code.length == 0 ||
        this.order.customer.shipping_country.length == 0
      );
    }
  },
  watch: {
    "show": function(val) {
      if (val) {
        this.fetchBillingItems();
      }
    }
  },
  created() {
    // this.fetchBillingItems();
  }
};
</script>
